import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { fmtDate, gbp, usePlan } from "@/lib/plan";

const ROWS = [
  ["AI-organised journal entries", "3 per 7 days", "Unlimited"],
  ["Journal entry length", "No limit", "No limit"],
  ["General quiz questions", "5 per day", "Unlimited"],
  ["Personalised technical quizzes", null, "Built from your journal"],
  ["Analytics", "Sessions, frequency, top positions", "Full: what's working, problems, progress, focus, quiz weak spots"],
  ["Journal history", "All your entries", "All your entries"],
];

function Compare() {
  return (
    <div className="card overflow-hidden" data-testid="pro-compare">
      <div className="grid grid-cols-[1.3fr_1fr_1fr] text-[11px] font-bold uppercase tracking-wider text-[#71717A] px-4 py-3 border-b border-[#1F1F23]">
        <span /> <span>Free</span> <span className="text-[#E50914]">Pro</span>
      </div>
      {ROWS.map(([label, free, pro]) => (
        <div key={label} className="grid grid-cols-[1.3fr_1fr_1fr] gap-2 px-4 py-3 text-[13px] border-b border-[#1F1F23] last:border-0">
          <span className="text-[#D4D4D8]">{label}</span>
          <span className="text-[#A1A1AA]">{free || <Lock size={13} className="text-[#52525B]" />}</span>
          <span className="text-white">{pro}</span>
        </div>
      ))}
    </div>
  );
}

function Offer({ plan, onSubscribe, busy }) {
  const o = plan.offer;
  const trial = o.trial_days > 0;
  const active = ["subscription", "trial"].includes(plan.source);
  if (active) {
    return (
      <div className="card p-5" data-testid="pro-already">
        <div className="font-semibold">You're on Pro</div>
        <p className="text-sm text-[#A1A1AA] mt-1">Manage or cancel your subscription from your Profile.</p>
        <Link to="/profile" className="btn-secondary mt-4" data-testid="pro-manage-link">Manage subscription</Link>
      </div>
    );
  }
  return (
    <div className="card p-5 border-[#E50914]/40" data-testid="pro-offer">
      <div className="flex items-end gap-2">
        <span className="font-display text-4xl font-semibold" data-testid="pro-price">{gbp(o.price)}</span>
        <span className="text-sm text-[#A1A1AA] pb-1">/ month</span>
      </div>
      {o.tier === "founder" ? (
        <p className="text-sm text-[#FF6B6B] mt-1" data-testid="pro-founder-note">Founder price for the first 20 subscribers ({plan.founder_slots_left} left). Locked in while your subscription stays active.</p>
      ) : (
        <p className="text-sm text-[#A1A1AA] mt-1">Monthly, cancel any time.</p>
      )}
      {trial ? (
        <div className="mt-4 rounded-xl bg-white/5 p-3 text-sm space-y-1" data-testid="pro-trial-terms">
          <div><span className="text-[#A1A1AA]">Free trial:</span> 7 days, card required</div>
          <div><span className="text-[#A1A1AA]">Trial ends:</span> {fmtDate(o.trial_end)}</div>
          <div><span className="text-[#A1A1AA]">First payment:</span> {gbp(o.price)} on {fmtDate(o.trial_end)}, then monthly</div>
          <div className="text-[#A1A1AA]">Cancel before the trial ends and you won't be charged.</div>
        </div>
      ) : o.first_charge_at ? (
        <p className="mt-4 text-sm text-[#A1A1AA]" data-testid="pro-first-charge">Your free Pro carries on until {fmtDate(o.first_charge_at)}. First payment: {gbp(o.price)} on {fmtDate(o.first_charge_at)}, then monthly. Cancel before then and you won't be charged.</p>
      ) : (
        <p className="mt-4 text-sm text-[#A1A1AA]" data-testid="pro-no-trial">You'll be charged {gbp(o.price)} today, then monthly on the same date.</p>
      )}
      <button data-testid="pro-subscribe-button" onClick={onSubscribe} disabled={busy} className="btn-primary w-full mt-5">
        {busy ? <Loader2 size={18} className="animate-spin" /> : trial ? "Start 7-day free trial" : plan.source === "early_supporter" ? "Keep Pro" : `Subscribe for ${gbp(o.price)}/month`}
      </button>
      <p className="text-[11px] text-[#71717A] mt-3 leading-relaxed">
        Renews monthly until you cancel. Cancel any time in Profile; you keep Pro until the end of the period you've paid for. Payments are processed securely by Stripe. By subscribing you agree to the <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>.
      </p>
    </div>
  );
}

export default function Upgrade() {
  const nav = useNavigate();
  const { data: plan } = usePlan();
  const [busy, setBusy] = useState(false);
  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errorMessage(err));
      setBusy(false);
    }
  };
  return (
    <div className="px-5 pt-6 space-y-5" data-testid="upgrade-page">
      <button onClick={() => nav(-1)} className="text-[#A1A1AA] flex items-center gap-1 text-sm" data-testid="upgrade-back"><ArrowLeft size={16} /> Back</button>
      <div>
        <div className="eyebrow mb-2">ROLLSMARTR Pro</div>
        <h1 className="font-display text-3xl font-semibold leading-tight">Train with a plan, not a guess.</h1>
        <p className="text-sm text-[#A1A1AA] mt-2">Unlimited AI journal, analytics built from your own sessions, and technical quizzes on exactly what you're working on.</p>
      </div>
      {plan ? <Offer plan={plan} onSubscribe={subscribe} busy={busy} /> : <div className="card h-48 animate-pulse" />}
      <Compare />
      <ul className="text-sm text-[#A1A1AA] space-y-2 pb-6">
        {["Your journal entries and quiz history are never deleted if you cancel.", "No ads. We don't sell your data."].map((t) => (
          <li key={t} className="flex gap-2"><Check size={16} className="text-[#E50914] shrink-0 mt-0.5" />{t}</li>
        ))}
      </ul>
    </div>
  );
}
